import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Quote, Sparkles, Download, Loader2, Mic, Square } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PDF_T = {
  fr: { export: "Exporter en PDF", subtitle: "Le Carnet d'Épiphanies", gen: "Création du PDF…", done: "Carnet PDF téléchargé ✨", err: "Export impossible.", empty: "Aucune épiphanie à exporter pour l'instant.", tagline: "Chaque page, une lumière retrouvée." },
  en: { export: "Export as PDF", subtitle: "The Book of Epiphanies", gen: "Building your PDF…", done: "PDF notebook downloaded ✨", err: "Export failed.", empty: "No epiphany to export yet.", tagline: "Every page, a light rediscovered." },
  es: { export: "Exportar en PDF", subtitle: "El Cuaderno de Epifanías", gen: "Creando el PDF…", done: "Cuaderno PDF descargado ✨", err: "No se pudo exportar.", empty: "Aún no hay epifanías para exportar.", tagline: "Cada página, una luz reencontrada." },
  nl: { export: "Exporteren als PDF", subtitle: "Het Boek van Epifanieën", gen: "PDF wordt gemaakt…", done: "PDF-notitieboek gedownload ✨", err: "Exporteren mislukt.", empty: "Nog geen epifanie om te exporteren.", tagline: "Elke pagina, een hervonden licht." },
};

function loadImageData(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ url: c.toDataURL("image/png"), w: img.width, h: img.height });
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function formatDate(iso, lng) {
  try {
    return new Date(iso).toLocaleDateString(lng || "fr-FR", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Carnet() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const P = PDF_T[lang] || PDF_T.fr;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retro, setRetro] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const uploadVoice = async (blob) => {
    setProcessing(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `epiphany.${ext}`);
      fd.append("lang", lang);
      const { data } = await api.post("/carnet/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data, ...prev]);
      toast.success(t("carnet.voiceDone"));
    } catch (e) {
      if (e?.response?.status === 402) toast.error(t("carnet.voiceUpsell"));
      else toast.error(t("carnet.voiceError"));
    } finally {
      setProcessing(false);
    }
  };

  const startRec = async () => {
    if (processing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size) await uploadVoice(blob);
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error(t("carnet.voiceDenied"));
    }
  };

  const stopRec = () => {
    const mr = mrRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  const toggleRec = () => (recording ? stopRec() : startRec());

  useEffect(() => {
    api.get("/carnet")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
    api.get(`/retrospective?lang=${lang}`)
      .then(({ data }) => setRetro(data.retrospective))
      .catch(() => {});
  }, [lang]);

  const exportPDF = async () => {
    if (exporting) return;
    if (!items.length) { toast(P.empty); return; }
    setExporting(true);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const W = 210, H = 297;
      const dark = [11, 14, 20], gold = [224, 138, 43], cream = [247, 240, 224];
      const ink = [42, 36, 28], gray = [120, 110, 96];

      const logo = await loadImageData("/cocoon-logo-glow.png");

      const drawHeaderBand = () => {
        doc.setFillColor(...dark); doc.rect(0, 0, W, 42, "F");
        if (logo) {
          const lh = 20, lw = lh * (logo.w / logo.h);
          doc.addImage(logo.url, "PNG", (W - lw) / 2, 5, lw, lh);
        }
        doc.setFont("times", "bold"); doc.setTextColor(...gold); doc.setFontSize(15);
        doc.text("COCOON MIND", W / 2, 32, { align: "center" });
        doc.setFont("times", "italic"); doc.setTextColor(...cream); doc.setFontSize(11);
        doc.text(P.subtitle, W / 2, 38.5, { align: "center" });
      };
      const drawFooter = (page) => {
        doc.setDrawColor(225, 215, 195); doc.setLineWidth(0.2);
        doc.line(20, H - 16, W - 20, H - 16);
        doc.setFont("times", "italic"); doc.setTextColor(...gray); doc.setFontSize(9);
        doc.text(P.tagline, 20, H - 11);
        doc.setFont("helvetica", "bold"); doc.setTextColor(...gold);
        doc.text("cocoonmind.com", W / 2, H - 11, { align: "center" });
        doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
        doc.text(String(page), W - 20, H - 11, { align: "right" });
      };

      // page background (cream)
      const paintPage = () => { doc.setFillColor(...cream); doc.rect(0, 0, W, H, "F"); };

      let page = 1;
      paintPage(); drawHeaderBand(); drawFooter(page);
      let y = 54;
      const left = 22, right = W - 22, maxW = right - left;

      items.forEach((it) => {
        const title = it.title || "";
        const content = it.content || "";
        doc.setFont("times", "bold"); doc.setFontSize(14);
        const titleLines = doc.splitTextToSize(title, maxW);
        doc.setFont("times", "italic"); doc.setFontSize(12);
        const bodyLines = doc.splitTextToSize(content, maxW);
        const blockH = 8 + titleLines.length * 6.4 + bodyLines.length * 5.8 + 8 + 8;

        if (y + blockH > H - 24) {
          page += 1; doc.addPage(); paintPage(); drawHeaderBand(); drawFooter(page); y = 54;
        }

        // gold quote glyph
        doc.setFont("times", "bold"); doc.setTextColor(...gold); doc.setFontSize(22);
        doc.text("\u201C", left, y);
        // title
        doc.setFont("times", "bold"); doc.setTextColor(...ink); doc.setFontSize(14);
        doc.text(titleLines, left + 8, y);
        y += titleLines.length * 6.4 + 2;
        // content
        doc.setFont("times", "italic"); doc.setTextColor(60, 54, 46); doc.setFontSize(12);
        doc.text(bodyLines, left + 8, y);
        y += bodyLines.length * 5.8 + 3;
        // date
        doc.setFont("helvetica", "normal"); doc.setTextColor(...gray); doc.setFontSize(8);
        doc.text((formatDate(it.created_at, i18n.language) || "").toUpperCase(), left + 8, y);
        y += 6;
        // divider
        doc.setDrawColor(230, 220, 200); doc.setLineWidth(0.2);
        doc.line(left + 8, y, right, y);
        y += 9;
      });

      doc.save("carnet-cocoon.pdf");
      toast.success(P.done);
    } catch (e) {
      toast.error(P.err);
    } finally { setExporting(false); }
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4 text-center">{t("carnet.eyebrow")}</p>
        <h2 className="font-display text-gradient-orange text-center text-4xl sm:text-5xl italic mb-3">{t("carnet.title")}</h2>
        <p className="text-foreground/50 max-w-lg mx-auto text-center mb-8 leading-relaxed">{t("carnet.subtitle")}</p>

        {/* Journal vocal — dicter une épiphanie */}
        <div className="flex flex-col items-center gap-2 mb-10">
          <button
            onClick={toggleRec}
            disabled={processing}
            data-testid="carnet-voice-btn"
            aria-label={t("carnet.voiceCta")}
            className={`inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-semibold text-sm transition-all disabled:opacity-70 ${
              recording
                ? "bg-red-500/15 border border-red-400/50 text-red-200"
                : "btn-frost-orange"
            }`}
          >
            {processing ? (
              <Loader2 size={17} className="animate-spin" />
            ) : recording ? (
              <Square size={16} className="fill-current" />
            ) : (
              <Mic size={17} />
            )}
            <span>
              {processing ? t("carnet.voiceProcessing") : recording ? t("carnet.voiceRecording") : t("carnet.voiceCta")}
            </span>
            {recording && <span className="w-2 h-2 rounded-full bg-red-400 animate-ping" />}
          </button>
        </div>

        {!loading && items.length > 0 && (
          <div className="flex justify-center mb-12">
            <button
              onClick={exportPDF}
              disabled={exporting}
              data-testid="carnet-export-pdf-btn"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-60"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {exporting ? P.gen : P.export}
            </button>
          </div>
        )}

        {retro && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="retrospective-card"
            className="glass rounded-2xl p-8 mb-12 relative overflow-hidden border border-primary/15"
          >
            <div className="chambre-aura" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="text-primary pulse-star" size={18} />
                <span className="uppercase tracking-[0.22em] text-xs text-primary/90">{t("carnet.retroEyebrow")}</span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl italic text-foreground/90 mb-4">{t("carnet.retroTitle")}</h3>
              <p className="font-display italic text-lg sm:text-xl text-foreground/80 leading-relaxed" data-testid="retrospective-text">
                {retro.text}
              </p>
              <p className="mt-6 text-xs uppercase tracking-wider text-foreground/35">
                {t("carnet.retroMeta", { topics: retro.topic_count, disciplines: retro.discipline_count, epiphanies: retro.epiphany_count })}
              </p>
            </div>
          </motion.div>
        )}

        {loading ? (
          <p className="font-display italic text-foreground/40 text-xl">{t("carnet.loading")}</p>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center">
            <p className="font-display italic text-foreground/50 text-2xl max-w-md mx-auto">
              {t("carnet.empty")}
            </p>
          </div>
        ) : (
          <div className="columns-1 md:columns-2 gap-6 [column-fill:_balance]">
            {items.map((it, i) => (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid="epiphany-card"
                style={{ animationDelay: `${(i % 4) * 0.7}s` }}
                className="glass frame-pulse rounded-2xl p-7 mb-6 break-inside-avoid"
              >
                <Quote className="text-primary/60 mb-4" size={22} />
                <h3 className="font-display text-2xl mb-3 text-foreground/90">{it.title}</h3>
                <p className="font-display italic text-lg text-foreground/70 leading-relaxed">
                  {it.content}
                </p>
                <p className="mt-5 text-xs uppercase tracking-wider text-foreground/30">
                  {formatDate(it.created_at, i18n.language)}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
